"use client";
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';

/* ── Wireframe side (Before) ── */
const WireframeSide = () => (
    <div className="w-full h-full flex flex-col" style={{ background: '#2e2e2e', padding: '20px 24px', gap: 0 }}>

        {/* ── Navbar ── */}
        <div className="flex items-center justify-between" style={{ marginBottom: 20 }}>
            {/* Logo text */}
            <span style={{ color: '#c0c0c0', fontSize: 13, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.05em' }}>LOGO</span>
            {/* Nav links + CTA */}
            <div className="flex items-center gap-3">
                {[52, 44, 48, 40].map((w, i) => (
                    <div key={i} style={{ background: '#484848', borderRadius: 3, width: w, height: 8 }} />
                ))}
                <div style={{ background: '#1a1a1a', borderRadius: 5, width: 52, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#fff', fontSize: 8, fontWeight: 700, fontFamily: 'monospace', letterSpacing: '0.05em' }}>CTA</span>
                </div>
            </div>
        </div>

        {/* ── Hero section: two-column ── */}
        <div className="flex gap-5 flex-1" style={{ minHeight: 0 }}>

            {/* Left column — text + buttons */}
            <div className="flex flex-col" style={{ flex: '0 0 52%', gap: 0 }}>
                {/* Heading blocks (3 lines, decreasing width) */}
                <div style={{ background: '#555', borderRadius: 4, width: '88%', height: 18, marginBottom: 8 }} />
                <div style={{ background: '#555', borderRadius: 4, width: '75%', height: 18, marginBottom: 8 }} />
                <div style={{ background: '#4a4a4a', borderRadius: 4, width: '60%', height: 18, marginBottom: 18 }} />

                {/* Paragraph text lines */}
                {[95, 90, 85, 70].map((w, i) => (
                    <div key={i} style={{ background: '#3d3d3d', borderRadius: 2, width: `${w}%`, height: 7, marginBottom: 6 }} />
                ))}

                {/* CTA buttons */}
                <div className="flex gap-3" style={{ marginTop: 20, marginBottom: 18 }}>
                    <div style={{ background: '#1a1a1a', borderRadius: 5, width: 90, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#fff', fontSize: 9, fontWeight: 700, fontFamily: 'monospace' }}>CTA</span>
                    </div>
                    <div style={{ background: 'transparent', borderRadius: 5, width: 90, height: 30, border: '1.5px solid #565656', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#888', fontSize: 9, fontWeight: 700, fontFamily: 'monospace' }}>CTA</span>
                    </div>
                </div>

                {/* Bottom bar + pagination dots */}
                <div className="flex items-center gap-3">
                    <div style={{ background: '#3d3d3d', borderRadius: 2, width: 90, height: 5 }} />
                    <div className="flex gap-2">
                        {[true, false, false].map((active, i) => (
                            <div key={i} style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: active ? '#666' : 'transparent',
                                border: '1.5px solid #565656',
                            }} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Right column — image placeholder */}
            <div style={{ flex: 1, background: '#3a3a3a', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Classic image placeholder icon */}
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="8" y="8" width="48" height="48" rx="4" stroke="#606060" strokeWidth="2" fill="none" />
                    {/* Mountain shape */}
                    <polyline points="10,46 26,26 36,36 44,28 54,46" stroke="#606060" strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round" />
                    {/* Sun circle */}
                    <circle cx="44" cy="20" r="5" stroke="#606060" strokeWidth="2" fill="none" />
                </svg>
            </div>
        </div>
    </div>
);

/* ── Final Design side (After) — mirrors wireframe layout exactly ── */
const FinalDesignSide = () => (
    <div className="w-full h-full flex flex-col" style={{ background: '#0a0f1e', padding: '20px 24px', gap: 0, position: 'relative', overflow: 'hidden' }}>

        {/* Subtle background glow */}
        <div style={{ position: 'absolute', top: '30%', right: '5%', width: 200, height: 200, background: 'radial-gradient(circle, rgba(206,255,26,0.05) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '10%', left: '30%', width: 160, height: 160, background: 'radial-gradient(circle, rgba(55,48,163,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

        {/* ── Navbar (mirrors wireframe nav) ── */}
        <div className="flex items-center justify-between" style={{ marginBottom: 20, position: 'relative' }}>
            {/* Logo */}
            <div className="flex items-center gap-1.5">
                <div style={{ background: '#ceff1a', borderRadius: 3, width: 12, height: 12 }} />
                <span style={{ color: '#ffffff', fontSize: 11, fontWeight: 800, fontFamily: 'sans-serif', letterSpacing: '0.06em' }}>Lead Workz</span>
            </div>
            {/* Nav links + CTA */}
            <div className="flex items-center gap-4">
                {['About', 'Services', 'Work', 'Contact'].map((item, i) => (
                    <span key={i} style={{ color: 'rgba(255,255,255,0.55)', fontSize: 8, fontWeight: 600, fontFamily: 'sans-serif', letterSpacing: '0.04em' }}>{item}</span>
                ))}
                <div style={{ background: '#ceff1a', borderRadius: 5, padding: '5px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#080400', fontSize: 8, fontWeight: 800, fontFamily: 'sans-serif', letterSpacing: '0.06em' }}>GET STARTED</span>
                </div>
            </div>
        </div>

        {/* ── Hero section: same two-column layout as wireframe ── */}
        <div className="flex gap-5 flex-1" style={{ minHeight: 0 }}>

            {/* Left column — same proportions as wireframe (52%) */}
            <div className="flex flex-col" style={{ flex: '0 0 52%', gap: 0 }}>

                {/* Tag label above heading */}
                <span style={{ color: '#ceff1a', fontSize: 8, fontWeight: 700, letterSpacing: '0.22em', fontFamily: 'sans-serif', textTransform: 'uppercase', marginBottom: 10 }}>
                    UI/UX DESIGN AGENCY
                </span>

                {/* Heading lines (same height/width as wireframe blocks) */}
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 800, lineHeight: 1.25, fontFamily: 'sans-serif', marginBottom: 18 }}>
                    Great Products Built<br />
                    Around{' '}
                    <span style={{ color: '#ceff1a' }}>People.</span>
                </div>

                {/* Paragraph text (same line count as wireframe) */}
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 8, fontFamily: 'sans-serif', lineHeight: 1.9, marginBottom: 20, maxWidth: '95%' }}>
                    We design digital experiences rooted in how<br />
                    people think, behave, and make decisions.<br />
                    Research-driven. Conversion-focused.<br />
                    Built to perform.
                </div>

                {/* CTA buttons (same position as wireframe) */}
                <div className="flex gap-3" style={{ marginBottom: 18 }}>
                    <div style={{ background: '#ceff1a', borderRadius: 5, padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#080400', fontSize: 9, fontWeight: 800, fontFamily: 'sans-serif', letterSpacing: '0.04em' }}>Book a Session →</span>
                    </div>
                    <div style={{ background: 'transparent', borderRadius: 5, padding: '7px 14px', border: '1.5px solid rgba(206,255,26,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 9, fontWeight: 700, fontFamily: 'sans-serif' }}>View Our Work</span>
                    </div>
                </div>

                {/* Progress bar + pagination dots (same as wireframe) */}
                <div className="flex items-center gap-3">
                    <div style={{ background: 'rgba(206,255,26,0.25)', borderRadius: 2, width: 90, height: 5, position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '40%', background: '#ceff1a', borderRadius: 2 }} />
                    </div>
                    <div className="flex gap-2">
                        {[true, false, false].map((active, i) => (
                            <div key={i} style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: active ? '#ceff1a' : 'transparent',
                                border: `1.5px solid ${active ? '#ceff1a' : 'rgba(255,255,255,0.25)'}`,
                            }} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Right column — UI/UX Design illustration */}
            <div style={{
                flex: 1,
                borderRadius: 8,
                background: 'linear-gradient(145deg, #0d1b2e 0%, #160d2e 100%)',
                border: '1px solid rgba(206,255,26,0.18)',
                overflow: 'hidden',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {/* Glow behind illustration */}
                <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: 160, height: 160, background: 'radial-gradient(circle, rgba(55,48,163,0.35) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '15%', right: '10%', width: 80, height: 80, background: 'radial-gradient(circle, rgba(206,255,26,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

                {/* UI/UX Design Concept SVG Illustration */}
                <svg width="100%" height="100%" viewBox="0 0 280 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', inset: 0 }}>

                    {/* ── Desktop screen frame ── */}
                    <rect x="30" y="14" width="220" height="132" rx="6" fill="#1e1e1e" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>

                    {/* ── Figma Top Bar ── */}
                    <rect x="30" y="14" width="220" height="15" rx="6" fill="#2c2c2c"/>
                    <rect x="30" y="22" width="220" height="7" fill="#2c2c2c"/>
                    {/* Figma logo mark (F) */}
                    <rect x="35" y="17" width="8" height="9" rx="1.5" fill="#ff7262" opacity="0.9"/>
                    <text x="39" y="24.5" textAnchor="middle" fill="white" fontSize="5.5" fontWeight="900" fontFamily="sans-serif">F</text>
                    {/* File name */}
                    <text x="80" y="24" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="5" fontFamily="sans-serif">lead workz  ·  Drafts</text>
                    {/* Top right: share btn */}
                    <rect x="218" y="17" width="26" height="8" rx="2" fill="#18a047"/>
                    <text x="231" y="23" textAnchor="middle" fill="white" fontSize="4.5" fontWeight="700" fontFamily="sans-serif">Share</text>
                    {/* Prototype play icon */}
                    <circle cx="210" cy="21" r="4" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.6"/>
                    <path d="M209 19.5 l3 1.5 -3 1.5z" fill="rgba(255,255,255,0.5)"/>

                    {/* ── Figma Toolbar (horizontal, dark pill — matches reference screenshot) ── */}
                    {/* Toolbar background pill */}
                    <rect x="75" y="118" width="100" height="17" rx="5" fill="#2c2c2c" stroke="rgba(255,255,255,0.08)" strokeWidth="0.6"/>

                    {/* Tool 1: Cursor (selected — blue bg) */}
                    <rect x="77" y="119.5" width="13" height="14" rx="3" fill="#5c6af5"/>
                    {/* Cursor arrow icon */}
                    <path d="M81 122.5 l0 7 2-2.5 1.5 3.5 1-0.5-1.5-3.5 2.5 0z" fill="white" stroke="#5c6af5" strokeWidth="0.3"/>

                    {/* Divider */}
                    <line x1="92" y1="121" x2="92" y2="134" stroke="rgba(255,255,255,0.12)" strokeWidth="0.6"/>

                    {/* Tool 2: Frame / Artboard (crosshair H) */}
                    <g transform="translate(95,127)">
                        <line x1="-4" y1="0" x2="4" y2="0" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round"/>
                        <line x1="0" y1="-4" x2="0" y2="4" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round"/>
                        <rect x="-2.5" y="-2.5" width="5" height="5" rx="0.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="0.8"/>
                    </g>

                    {/* Tool 3: Shape (square outline) */}
                    <rect x="106" y="122.5" width="9" height="9" rx="1.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2"/>

                    {/* Tool 4: Pen / Pencil */}
                    <g transform="translate(122,127)">
                        <path d="M-4,4 L2,-3 L4,-1 L-2,5z" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1" strokeLinejoin="round"/>
                        <path d="M-4,4 L-5,5 L-4,6 L-3,5z" fill="rgba(255,255,255,0.4)"/>
                        <line x1="1" y1="-2" x2="3" y2="0" stroke="rgba(255,255,255,0.4)" strokeWidth="0.8"/>
                    </g>

                    {/* Divider */}
                    <line x1="130" y1="121" x2="130" y2="134" stroke="rgba(255,255,255,0.12)" strokeWidth="0.6"/>

                    {/* Tool 5: Text (T) */}
                    <text x="137" y="131" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="8" fontWeight="600" fontFamily="sans-serif">T</text>

                    {/* Tool 6: Comment (speech bubble) */}
                    <g transform="translate(148,127)">
                        <path d="M-4,-4 Q-4,-6 -2,-6 L4,-6 Q6,-6 6,-4 L6,1 Q6,3 4,3 L0,3 L-3,6 L-3,3 L-4,3 Q-6,3 -6,1 L-6,-4 Q-6,-6 -4,-6z" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1" strokeLinejoin="round"/>
                    </g>

                    {/* Tool 7: Component / Grid+ */}
                    <g transform="translate(161,127)">
                        <rect x="-5" y="-5" width="4" height="4" rx="0.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1"/>
                        <rect x="1" y="-5" width="4" height="4" rx="0.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1"/>
                        <rect x="-5" y="1" width="4" height="4" rx="0.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1"/>
                        <line x1="3" y1="1" x2="3" y2="5" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round"/>
                        <line x1="1" y1="3" x2="5" y2="3" stroke="rgba(255,255,255,0.55)" strokeWidth="1.2" strokeLinecap="round"/>
                    </g>

                    {/* ── Inside screen: Design tool interface ── */}
                    {/* Left sidebar - layers panel */}
                    <rect x="30" y="29" width="26" height="117" fill="#252525"/>
                    {/* Figma left panel icons */}
                    <rect x="34" y="33" width="18" height="3" rx="1" fill="rgba(255,255,255,0.1)"/>
                    <rect x="34" y="40" width="14" height="2.5" rx="1" fill="rgba(206,255,26,0.5)"/>
                    <rect x="34" y="46" width="16" height="2.5" rx="1" fill="rgba(255,255,255,0.07)"/>
                    <rect x="34" y="52" width="12" height="2.5" rx="1" fill="rgba(255,255,255,0.07)"/>
                    <rect x="34" y="58" width="15" height="2.5" rx="1" fill="rgba(55,48,163,0.6)"/>
                    <rect x="34" y="64" width="13" height="2.5" rx="1" fill="rgba(255,255,255,0.05)"/>
                    <rect x="34" y="70" width="17" height="2.5" rx="1" fill="rgba(255,255,255,0.05)"/>
                    <rect x="34" y="76" width="11" height="2.5" rx="1" fill="rgba(255,255,255,0.05)"/>
                    {/* Separator */}
                    <line x1="56" y1="29" x2="56" y2="146" stroke="rgba(255,255,255,0.05)" strokeWidth="0.8"/>

                    {/* ── Main canvas area ── */}
                    <rect x="56" y="29" width="140" height="117" fill="#1a1a1a"/>

                    {/* Hero card on canvas */}
                    <rect x="62" y="35" width="110" height="64" rx="3" fill="#0a1628" stroke="rgba(206,255,26,0.15)" strokeWidth="0.8"/>
                    {/* Heading block */}
                    <rect x="67" y="42" width="55" height="6" rx="2" fill="#ceff1a" opacity="0.9"/>
                    <rect x="67" y="52" width="72" height="4" rx="1.5" fill="rgba(255,255,255,0.22)"/>
                    <rect x="67" y="59" width="58" height="4" rx="1.5" fill="rgba(255,255,255,0.12)"/>
                    {/* Mini image placeholder */}
                    <rect x="128" y="40" width="36" height="36" rx="2.5" fill="rgba(55,48,163,0.4)" stroke="rgba(55,48,163,0.5)" strokeWidth="0.7"/>
                    <path d="M137 58 l5-8 4 5 3-3 7 9H137z" fill="rgba(206,255,26,0.25)"/>
                    <circle cx="144" cy="48" r="3" fill="rgba(206,255,26,0.35)"/>
                    {/* CTA buttons on canvas */}
                    <rect x="67" y="67" width="34" height="10" rx="2.5" fill="#ceff1a"/>
                    <rect x="104" y="67" width="28" height="10" rx="2.5" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.14)" strokeWidth="0.7"/>

                    {/* Component cards row */}
                    <rect x="62" y="105" width="30" height="22" rx="2.5" fill="rgba(206,255,26,0.07)" stroke="rgba(206,255,26,0.18)" strokeWidth="0.7"/>
                    <rect x="66" y="109" width="22" height="3.5" rx="1" fill="rgba(206,255,26,0.4)"/>
                    <rect x="66" y="115" width="18" height="2.5" rx="1" fill="rgba(255,255,255,0.1)"/>
                    <rect x="66" y="121" width="14" height="2.5" rx="1" fill="rgba(255,255,255,0.07)"/>

                    <rect x="96" y="105" width="30" height="22" rx="2.5" fill="rgba(55,48,163,0.14)" stroke="rgba(55,48,163,0.3)" strokeWidth="0.7"/>
                    <rect x="100" y="109" width="22" height="3.5" rx="1" fill="rgba(55,48,163,0.65)"/>
                    <rect x="100" y="115" width="16" height="2.5" rx="1" fill="rgba(255,255,255,0.09)"/>
                    <rect x="100" y="121" width="20" height="2.5" rx="1" fill="rgba(255,255,255,0.06)"/>

                    <rect x="130" y="105" width="42" height="22" rx="2.5" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.07)" strokeWidth="0.7"/>
                    <circle cx="143" cy="113" r="3.5" fill="rgba(206,255,26,0.12)" stroke="rgba(206,255,26,0.3)" strokeWidth="0.7"/>
                    <rect x="150" y="109" width="16" height="2.5" rx="1" fill="rgba(255,255,255,0.13)"/>
                    <rect x="150" y="115" width="12" height="2.5" rx="1" fill="rgba(255,255,255,0.07)"/>
                    <rect x="133" y="121" width="36" height="3.5" rx="1.5" fill="rgba(206,255,26,0.22)"/>

                    {/* Right properties panel */}
                    <rect x="196" y="29" width="34" height="117" fill="#252525"/>
                    <line x1="196" y1="29" x2="196" y2="146" stroke="rgba(255,255,255,0.05)" strokeWidth="0.8"/>
                    {/* Inspect / Plugins tabs */}
                    <text x="205" y="37" fill="rgba(255,255,255,0.7)" fontSize="4.5" fontWeight="700" fontFamily="sans-serif">Inspect</text>
                    <text x="218" y="37" fill="rgba(255,255,255,0.3)" fontSize="4.5" fontFamily="sans-serif">Plugins</text>
                    <line x1="200" y1="39" x2="214" y2="39" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8"/>
                    {/* Properties */}
                    <rect x="199" y="43" width="28" height="2.5" rx="1" fill="rgba(255,255,255,0.1)"/>
                    {/* Color swatches */}
                    <circle cx="202" cy="52" r="3.5" fill="#ceff1a"/>
                    <circle cx="211" cy="52" r="3.5" fill="#3730a3"/>
                    <circle cx="220" cy="52" r="3.5" fill="rgba(255,255,255,0.5)"/>
                    <rect x="199" y="59" width="22" height="2.5" rx="1" fill="rgba(255,255,255,0.07)"/>
                    <rect x="199" y="65" width="16" height="2.5" rx="1" fill="rgba(206,255,26,0.3)"/>
                    <rect x="199" y="71" width="20" height="2.5" rx="1" fill="rgba(255,255,255,0.05)"/>
                    <rect x="199" y="77" width="14" height="2.5" rx="1" fill="rgba(255,255,255,0.05)"/>
                    {/* Layout box */}
                    <rect x="199" y="84" width="26" height="18" rx="2" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.09)" strokeWidth="0.6"/>
                    <text x="205" y="90" fill="rgba(255,255,255,0.25)" fontSize="4" fontFamily="sans-serif">Border</text>
                    <text x="205" y="97" fill="rgba(206,255,26,0.5)" fontSize="4" fontFamily="sans-serif">Padding</text>
                    <rect x="201" y="99" width="22" height="1.5" rx="0.5" fill="rgba(206,255,26,0.3)"/>

                    {/* ── Monitor stand ── */}
                    <rect x="126" y="146" width="18" height="7" rx="1" fill="#1e1e1e"/>
                    <rect x="116" y="153" width="38" height="3.5" rx="2" fill="#1e1e1e" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5"/>

                    {/* ── Selection handles on hero card ── */}
                    <rect x="60" y="33" width="114" height="68" rx="3" fill="none" stroke="#5c6af5" strokeWidth="0.8" strokeDasharray="3 2" opacity="0.6"/>
                    <rect x="58" y="31" width="5" height="5" rx="1" fill="#5c6af5" opacity="0.9"/>
                    <rect x="171" y="31" width="5" height="5" rx="1" fill="#5c6af5" opacity="0.9"/>
                    <rect x="58" y="97" width="5" height="5" rx="1" fill="#5c6af5" opacity="0.9"/>
                    <rect x="171" y="97" width="5" height="5" rx="1" fill="#5c6af5" opacity="0.9"/>
                    {/* Mid-edge handles */}
                    <rect x="114" y="31" width="5" height="5" rx="1" fill="#5c6af5" opacity="0.9"/>
                    <rect x="114" y="97" width="5" height="5" rx="1" fill="#5c6af5" opacity="0.9"/>

                    {/* Cursor / pointer tool */}
                    <g transform="translate(150, 84)">
                        <path d="M0 0 L0 12 L3.5 8.5 L6 14 L7.5 13.5 L5 7.5 L9 7.5 Z" fill="white" stroke="#1a1a1a" strokeWidth="0.7"/>
                    </g>

                    {/* ── Floating label "1920 × 3492" (like in Figma inspect) ── */}
                    <rect x="152" y="72" width="44" height="11" rx="2.5" fill="#5c6af5"/>
                    <text x="174" y="80" textAnchor="middle" fill="white" fontSize="5" fontWeight="700" fontFamily="sans-serif">1920 × 3492</text>

                    {/* ── Mobile phone mockup (right side, outside main screen) ── */}
                    <rect x="222" y="30" width="36" height="62" rx="5" fill="#252525" stroke="rgba(206,255,26,0.25)" strokeWidth="0.8"/>
                    <rect x="225" y="37" width="30" height="47" rx="2" fill="#1a1a1a"/>
                    {/* Mobile notch */}
                    <rect x="231" y="31.5" width="10" height="3" rx="1.5" fill="#252525"/>
                    {/* Mobile screen content */}
                    <rect x="227" y="40" width="26" height="5" rx="1.5" fill="#ceff1a" opacity="0.8"/>
                    <rect x="227" y="48" width="20" height="2.5" rx="1" fill="rgba(255,255,255,0.18)"/>
                    <rect x="227" y="53" width="16" height="2.5" rx="1" fill="rgba(255,255,255,0.1)"/>
                    <rect x="227" y="60" width="26" height="14" rx="2" fill="rgba(55,48,163,0.3)" stroke="rgba(55,48,163,0.45)" strokeWidth="0.5"/>
                    <rect x="229" y="77" width="11" height="4" rx="1.5" fill="#ceff1a" opacity="0.9"/>
                    {/* Mobile home bar */}
                    <rect x="232" y="87" width="10" height="2" rx="1" fill="rgba(255,255,255,0.25)"/>

                    {/* ── Color palette strip (bottom) ── */}
                    <rect x="30" y="162" width="78" height="20" rx="3.5" fill="#2c2c2c" stroke="rgba(255,255,255,0.07)" strokeWidth="0.7"/>
                    <text x="69" y="170" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="4.5" fontFamily="sans-serif">COLOR PALETTE</text>
                    {[['#ceff1a',38],['#3730a3',50],['#0a1628',62],['#ffffff',74],['#ff6b6b',86]].map(([c,x],i) => (
                        <circle key={i} cx={x as number} cy={176} r="4" fill={c as string} stroke="rgba(255,255,255,0.12)" strokeWidth="0.5"/>
                    ))}

                    {/* ── Annotation / note ── */}
                    <rect x="116" y="160" width="76" height="26" rx="3" fill="#2c2c2c" stroke="rgba(206,255,26,0.18)" strokeWidth="0.7"/>
                    <line x1="120" y1="168" x2="188" y2="168" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5"/>
                    <text x="154" y="166" textAnchor="middle" fill="rgba(206,255,26,0.65)" fontSize="5" fontWeight="700" fontFamily="sans-serif">User Flow</text>
                    <text x="154" y="173" textAnchor="middle" fill="rgba(255,255,255,0.28)" fontSize="4" fontFamily="sans-serif">Onboarding → Dashboard</text>
                    <text x="154" y="180" textAnchor="middle" fill="rgba(255,255,255,0.18)" fontSize="4" fontFamily="sans-serif">3 steps · 92% completion</text>

                    {/* ── Connection line annotation → screen ── */}
                    <path d="M192 170 Q210 160 222 100" stroke="rgba(206,255,26,0.18)" strokeWidth="0.7" fill="none" strokeDasharray="2 2"/>
                    <circle cx="222" cy="100" r="1.8" fill="rgba(206,255,26,0.4)"/>

                </svg>
            </div>
        </div>
    </div>
);

export default function UXComparison() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [sliderPos, setSliderPos] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    const updateSlider = useCallback((clientX: number) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const pct = Math.max(5, Math.min(95, (x / rect.width) * 100));
        setSliderPos(pct);
        if (!hasInteracted) setHasInteracted(true);
    }, [hasInteracted]);

    const onMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        updateSlider(e.clientX);
    }, [updateSlider]);

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        setIsDragging(true);
        updateSlider(e.touches[0].clientX);
    }, [updateSlider]);

    useEffect(() => {
        const onMouseMove = (e: MouseEvent) => { if (isDragging) updateSlider(e.clientX); };
        const onTouchMove = (e: TouchEvent) => { if (isDragging) updateSlider(e.touches[0].clientX); };
        const onUp = () => setIsDragging(false);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onTouchMove);
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [isDragging, updateSlider]);

    /* Idle hint animation — nudges handle left-right until user drags */
    useEffect(() => {
        if (hasInteracted) return;
        let frame: number;
        let start: number | null = null;
        const animate = (ts: number) => {
            if (!start) start = ts;
            const t = (ts - start) / 1000;
            const nudge = Math.sin(t * 1.2) * 8;
            setSliderPos(50 + nudge);
            frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, [hasInteracted]);

    return (
        <section className="relative overflow-hidden py-10 md:py-24 px-4 md:px-16 lg:px-24" style={{ background: '#080808' }}>
            <div className="max-w-[1400px] mx-auto flex flex-col gap-8 md:gap-14 relative z-10">

                {/* Heading */}
                <div className="flex flex-col items-center gap-4 text-center">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase"
                        style={{ color: 'rgba(206,255,26,0.6)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        The Transformation
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold tracking-wide leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                        Before{' '}
                        <span style={{ color: '#ceff1a' }}>&</span>
                        {' '}After
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-base md:text-lg max-w-xl"
                        style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.02em' }}>
                        Drag the handle to see how we transform wireframes into polished, high-performing digital experiences.
                    </motion.p>
                </div>

                {/* Slider container */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
                    className="relative mx-auto w-full"
                    style={{ maxWidth: 900 }}
                >
                    {/* Outer glow frame */}
                    <div style={{ borderRadius: 20, padding: 2, background: 'linear-gradient(135deg, rgba(206,255,26,0.2) 0%, rgba(55,48,163,0.2) 100%)' }}>
                        <div
                            ref={containerRef}
                            className="relative overflow-hidden select-none"
                            style={{ borderRadius: 18, height: 'clamp(240px, 55vw, 460px)', cursor: isDragging ? 'grabbing' : 'grab' }}
                            onMouseDown={onMouseDown}
                            onTouchStart={onTouchStart}
                        >
                            {/* ── AFTER side (full width, behind) ── */}
                            <div className="absolute inset-0">
                                <FinalDesignSide />
                            </div>

                            {/* ── BEFORE side (clipped to slider pos) ── */}
                            <div
                                className="absolute inset-0 overflow-hidden"
                                style={{ width: `${sliderPos}%`, transition: isDragging ? 'none' : 'width 0.05s', zIndex: 2 }}
                            >
                                <div style={{ width: containerRef.current?.offsetWidth || 900, height: '100%' }}>
                                    <WireframeSide />
                                </div>
                            </div>

                            {/* ── Divider line ── */}
                            <div
                                className="absolute top-0 bottom-0 w-px pointer-events-none"
                                style={{
                                    left: `${sliderPos}%`,
                                    zIndex: 3,
                                    background: 'linear-gradient(180deg, transparent 0%, #ceff1a 20%, #ceff1a 80%, transparent 100%)',
                                    boxShadow: '0 0 12px rgba(206,255,26,0.6)',
                                    transition: isDragging ? 'none' : 'left 0.05s',
                                }}
                            />

                            {/* ── Drag handle ── */}
                            <div
                                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 flex items-center justify-center"
                                style={{
                                    left: `${sliderPos}%`,
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    background: '#ceff1a',
                                    boxShadow: isDragging
                                        ? '0 0 0 4px rgba(206,255,26,0.3), 0 0 30px rgba(206,255,26,0.5)'
                                        : '0 0 0 2px rgba(206,255,26,0.4), 0 4px 20px rgba(0,0,0,0.5)',
                                    cursor: 'grab',
                                    transition: isDragging ? 'box-shadow 0.1s' : 'left 0.05s, box-shadow 0.2s',
                                }}
                            >
                                {/* Left-right arrow icon */}
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M7 5l-5 5 5 5" stroke="#080400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M13 5l5 5-5 5" stroke="#080400" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>

                            {/* ── Before label (solid dark pill) ── */}
                            <div
                                className="absolute pointer-events-none"
                                style={{
                                    bottom: 20,
                                    left: 20,
                                    zIndex: 10,
                                    opacity: sliderPos > 12 ? 1 : 0,
                                    transition: 'opacity 0.3s',
                                }}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        background: '#111111',
                                        color: '#ffffff',
                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        padding: '8px 20px',
                                        borderRadius: 999,
                                    }}
                                >
                                    Before
                                </span>
                            </div>

                            {/* ── After label (plain text) ── */}
                            <div
                                className="absolute pointer-events-none"
                                style={{
                                    bottom: 20,
                                    right: 20,
                                    zIndex: 10,
                                    opacity: sliderPos < 88 ? 1 : 0,
                                    transition: 'opacity 0.3s',
                                }}
                            >
                                <span
                                    style={{
                                        display: 'inline-block',
                                        color: '#ffffff',
                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    After
                                </span>
                            </div>

                            {/* ── Hint text (fades after interaction) ── */}
                            {!hasInteracted && (
                                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 pointer-events-none flex items-center gap-2"
                                    style={{ opacity: 0.6 }}>
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M5 3l-4 4 4 4" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M9 3l4 4-4 4" stroke="#ceff1a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontFamily: 'var(--font-manrope), Manrope, sans-serif', letterSpacing: '0.08em' }}>
                                        Drag to compare
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* ── Figma-style Toolbar ── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.35 }}
                    className="flex justify-center"
                >
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            background: '#2c2c2c',
                            borderRadius: 12,
                            padding: '10px 16px',
                            gap: 4,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        }}
                    >
                        {/* Tool 1: Cursor (selected — blue bg) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: '#5c6af5', borderRadius: 7, padding: '6px 8px' }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M2 2L2 11L5.2 8L7 12.5L8.8 11.8L7 7.4L10.5 7.4Z" fill="white" stroke="#5c6af5" strokeWidth="0.4" strokeLinejoin="round"/>
                            </svg>
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path d="M2 3l2 2 2-2" stroke="rgba(255,255,255,0.6)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Divider */}
                        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />

                        {/* Tool 2: Frame (crosshair) */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '6px 8px', borderRadius: 7 }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <line x1="7" y1="1" x2="7" y2="13" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" strokeLinecap="round"/>
                                <line x1="1" y1="7" x2="13" y2="7" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" strokeLinecap="round"/>
                                <rect x="3.5" y="3.5" width="7" height="7" rx="1" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" fill="none"/>
                            </svg>
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path d="M2 3l2 2 2-2" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Tool 3: Rectangle */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '6px 8px', borderRadius: 7 }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <rect x="2" y="2" width="10" height="10" rx="1.5" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" fill="none"/>
                            </svg>
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path d="M2 3l2 2 2-2" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Tool 4: Pen/Pencil */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '6px 8px', borderRadius: 7 }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M9.5 2.5 L11.5 4.5 L5 11 L2 12 L3 9 Z" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" strokeLinejoin="round" fill="none"/>
                                <line x1="8" y1="4" x2="10" y2="6" stroke="rgba(255,255,255,0.4)" strokeWidth="1"/>
                            </svg>
                            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path d="M2 3l2 2 2-2" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Divider */}
                        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />

                        {/* Tool 5: Text */}
                        <div style={{ padding: '6px 10px', borderRadius: 7 }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <line x1="2" y1="3" x2="12" y2="3" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" strokeLinecap="round"/>
                                <line x1="7" y1="3" x2="7" y2="12" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" strokeLinecap="round"/>
                            </svg>
                        </div>

                        {/* Tool 6: Comment */}
                        <div style={{ padding: '6px 10px', borderRadius: 7 }}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M2 2.5 Q2 1 3.5 1 L10.5 1 Q12 1 12 2.5 L12 8 Q12 9.5 10.5 9.5 L7.5 9.5 L4.5 13 L4.5 9.5 L3.5 9.5 Q2 9.5 2 8 Z" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" fill="none" strokeLinejoin="round"/>
                            </svg>
                        </div>

                        {/* Tool 7: Components/Grid+ */}
                        <div style={{ padding: '6px 10px', borderRadius: 7 }}>
                            <svg width="16" height="14" viewBox="0 0 16 14" fill="none">
                                <rect x="1" y="1" width="6" height="6" rx="1" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" fill="none"/>
                                <rect x="9" y="1" width="6" height="6" rx="1" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" fill="none"/>
                                <rect x="1" y="9" width="6" height="6" rx="1" stroke="rgba(255,255,255,0.65)" strokeWidth="1.2" fill="none"/>
                                <line x1="12" y1="9" x2="12" y2="15" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" strokeLinecap="round"/>
                                <line x1="9" y1="12" x2="15" y2="12" stroke="rgba(255,255,255,0.65)" strokeWidth="1.4" strokeLinecap="round"/>
                            </svg>
                        </div>
                    </div>
                </motion.div>

            </div>
        </section>
    );
}
